/**
 * Web Push subscription helper for STAKES.
 *
 * - Registers /sw.js once per origin
 * - Asks the user for Notification permission (only when called from a user
 *   gesture path; browsers block silent auto-prompts)
 * - Subscribes the device to push and forwards the subscription to the
 *   STAKES backend, tagged to the currently-logged-in user.
 */
import { api } from "@/lib/api";

const SW_PATH = "/sw.js";
const LS_KEY = "stakes_push_user_id"; // tracks last subscribed user-id to avoid double-subs

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;
}

export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (existing) return existing;
    return await navigator.serviceWorker.register(SW_PATH);
  } catch {
    return null;
  }
}

/**
 * Prompt for notification permission and subscribe.
 * @param {string} userId — the active STAKES profile id (used as the push target)
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function enablePushForUser(userId) {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  if (!userId) return { ok: false, reason: "no_user" };

  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: "sw_failed" };

  if (Notification.permission === "denied") return { ok: false, reason: "denied" };
  if (Notification.permission !== "granted") {
    const r = await Notification.requestPermission();
    if (r !== "granted") return { ok: false, reason: "denied" };
  }

  try {
    let publicKey;
    try {
      const r = await api.getVapidKey();
      publicKey = r?.publicKey;
    } catch (apiErr) {
      // Backend returns 503 with an actionable `detail` when VAPID env
      // vars are missing on the deployed server. Surface that so the
      // bell toast can tell the operator exactly what to fix.
      const status = apiErr?.response?.status;
      const detail = apiErr?.response?.data?.detail;
      // eslint-disable-next-line no-console
      console.error("[push] vapid-public-key fetch failed:", status, detail, apiErr);
      if (status === 503) return { ok: false, reason: "server_not_configured", detail };
      return { ok: false, reason: "key_fetch_failed", detail: detail || apiErr?.message };
    }
    if (!publicKey) return { ok: false, reason: "no_key" };
    const sub = await reg.pushManager.getSubscription() || await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    await api.subscribePush(userId, sub.toJSON());
    try { localStorage.setItem(LS_KEY, userId); } catch { /* ignore */ }
    return { ok: true };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[push] subscribe failed:", e);
    return { ok: false, reason: e?.message || "subscribe_failed" };
  }
}

export function lastSubscribedUserId() {
  try { return localStorage.getItem(LS_KEY) || null; } catch { return null; }
}
